'use client';

import { c as _c } from "react/compiler-runtime";
import { motion, Variants } from 'framer-motion';
import Link from 'next/link';
import { ArrowRight, Sparkles } from 'lucide-react';
import { CATEGORIES } from '@/lib/categories';
import { AnimatedIcon } from '@/components/ui/AnimatedIcon';
import * as Icons from 'lucide-react';
const containerVariants: Variants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};
export function CategoryGrid() {
  const $ = _c(10);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute inset-0 bg-muted/20 pointer-events-none" />;
    t1 = <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y: 20
    };
    t3 = {
      opacity: 1,
      y: 0
    };
    t4 = {
      once: true
    };
    t5 = {
      duration: 0.6
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"><Sparkles className="w-4 h-4 mr-2" />Knowledge Hub</span>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="text-center mb-16 md:mb-24"><motion.div initial={t2} whileInView={t3} viewport={t4} transition={t5}>{t6}<h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Explore by <span className="gradient-text-primary">Topic</span></h2><p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">Curated collections of expert insights, tutorials, and deep dives across our core domains.</p></motion.div></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      once: true,
      margin: "-50px"
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <section className="relative py-24 md:py-32 overflow-hidden">{t0}{t1}<div className="container relative z-10">{t7}<motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={t8} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">{CATEGORIES.map(_temp)}</motion.div></div></section>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function _temp(category) {
  return <motion.div key={category.slug} variants={itemVariants} className="h-full"><Link href={`/${category.slug}`} className="group block h-full relative"><div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 dark:from-white/5 dark:to-white/[0.02] rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" /><div className="relative h-full p-8 rounded-3xl bg-card/50 backdrop-blur-md border border-border/50 hover:border-primary/30 transition-all duration-300 overflow-hidden group-hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:group-hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]"><div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-300`} /><div className="relative z-10 flex flex-col h-full"><div className="mb-6 flex justify-between items-start"><div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient} p-[1px] shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}><div className="w-full h-full rounded-2xl bg-card/90 backdrop-blur-sm flex items-center justify-center p-3"><AnimatedIcon name={category.icon} className="w-full h-full" /></div></div><div className="w-10 h-10 rounded-full border border-border/50 flex items-center justify-center group-hover:border-primary/30 group-hover:bg-primary/5 transition-colors"><ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors -rotate-45 group-hover:rotate-0 transform duration-300" /></div></div><h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">{category.name}</h3><p className="text-muted-foreground mb-6 leading-relaxed flex-grow">{category.description}</p><div className="flex items-center text-sm font-medium text-primary/80 group-hover:text-primary transition-colors"><span>Browse Articles</span><div className="h-[1px] flex-grow mx-4 bg-border group-hover:bg-primary/20 transition-colors" /></div></div></div></Link></motion.div>;
}